import * as React from "react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import HeaderPanel from "../layout/HeaderPanel";
import { FooterProgressBarProps } from "../layout/FooterPanel";
import { CoObjectSummary } from "../interfaces/CoObject";
import { getPalette, RuleResultContent } from "./RuleResultItem";
import { RunStatusIndicator, usePrefersReducedMotion } from "./RunStatusIndicator";
import { isExpandableResult, isNonTerminal } from "./runStatus";
import AccordionListPanel, { AccordionItemData } from "../generic/accordion/AccordionListPanel";
import { RESULTS_ORDERING_MODE } from "./runConfig";
import { FLIP_ROW_MARKER_ATTRIBUTE, useFlipReorder } from "./useFlipReorder";
import type { ConsistencyTagErrorKind } from "../../utils/helpers/RulesRunnerHelper";
import type { TableTotalsResultModel } from "./tableTotals/tableTotalsResult.types";

export type RunStatusType = "VERIFIED" | "FAILED" | "ERROR" | "RUNNING" | "QUEUED" | "CANCELED";

export interface ConsistencyTagInfo {
  label: string;
  status: RunStatusType;
  objects?: CoObjectSummary[];
  errorMessage?: string;
  errorKind?: ConsistencyTagErrorKind;
}

export type RunInfo = {
  status: RunStatusType;
  timestamp?: string;
  localExpression?: ReactNode;
  evaluatedExpression?: ReactNode;
  /** Structured per-tag results — present for CONSISTENCY rules */
  consistencyTags?: ConsistencyTagInfo[];
  /** Script-level message — error or informational — present for CUSTOM rules */
  message?: string;
  /** Structured result for TABLE_TOTALS rules — present only for TABLE_TOTALS results */
  tableTotals?: TableTotalsResultModel;
};

export interface RunResult {
  id: string;
  ruleId: string;
  title: string;
  /** Discriminates CONSISTENCY vs CUSTOM vs TABLE_TOTALS rendering */
  ruleType?: "CONSISTENCY" | "CUSTOM" | "TABLE_TOTALS";
  info: RunInfo;
}

export interface RulesResultPanelProps {
  results?: RunResult[];
  onDelete?: (ruleId: string) => void;
  onShowOnDocument?: (payload: CoObjectSummary) => void;
  onBack: () => void;
}

interface StatusLabelPresentation {
  /** CSS class driving the shimmer animation (`rrp-shimmer-blue`/`rrp-shimmer-gray`), or undefined when static. */
  className?: string;
  style: React.CSSProperties;
}

/**
 * Computes the label's className/style for a given status. Pulled out of `RunStatusEnd` as a pure
 * function so the branching is easy to test/read in isolation. `isNonTerminal` gates whether a
 * status shimmers at all (RUNNING/QUEUED); only the RUNNING-vs-QUEUED sub-choice needs a direct
 * status check.
 */
function getStatusLabelPresentation(
  status: RunStatusType,
  accent: string,
  prefersReducedMotion: boolean
): StatusLabelPresentation {
  const base: React.CSSProperties = { fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" };

  if (status === "CANCELED") {
    // Terminal, but styled distinctly (gray italic) — never shimmers.
    return { style: { ...base, color: accent, fontStyle: "italic" } };
  }

  if (isNonTerminal(status) && !prefersReducedMotion) {
    // RUNNING -> blue, QUEUED -> gray. No inline `color`: the shimmer classes paint text via
    // `background-clip: text` + `color: transparent` (colors.css); an inline color would hide the
    // gradient. `--rrp-shimmer-base` carries the accent tone into that CSS gradient.
    return {
      className: status === "RUNNING" ? "rrp-shimmer-blue" : "rrp-shimmer-gray",
      style: { ...base, ["--rrp-shimmer-base" as unknown as string]: accent },
    };
  }

  // VERIFIED/FAILED/ERROR, and the reduced-motion fallback for RUNNING/QUEUED: static, no shimmer.
  return { style: { ...base, color: accent } };
}

/**
 * Right-side status label (shimmer) + expand chevron, both rendered via the `itemEnd` render prop.
 * Kept as a real component so `usePrefersReducedMotion`'s hooks are safe regardless of how many
 * rows render.
 *
 * Stamps `FLIP_ROW_MARKER_ATTRIBUTE` onto its outer span with the row's stable `rowId` — the only
 * per-row identity marker `useFlipReorder` needs to walk up to the row's `.alp-item` root and
 * animate the whole row, without touching the shared `AccordionListPanel`.
 *
 * `onActivate` makes the status area clickable via a separate, absolutely-positioned, `aria-hidden`
 * overlay `<span>` — not an `onClick` on the label/outer span directly, for three reasons: (1) a
 * nested `<button>`/`role="button"` here would be invalid inside `AccordionHeader`'s own native
 * `<button>` root; (2) a non-interactive `onClick` element needs `aria-hidden` to avoid a
 * SonarQube finding, and only an empty overlay can safely take that without hiding the visible
 * label from assistive tech; (3) the row is already fully keyboard-operable via the native header
 * button — this overlay only widens the mouse hit target. Only wired up when the row
 * `isExpandableResult`, so non-expandable rows have no clickable overlay.
 *
 * The chevron is a static Fluent icon (never rotated) — threading `expanded` in for a rotation
 * animation isn't worth the extra re-render surface.
 */
const RunStatusEnd: React.FC<{ status: RunStatusType; rowId: string; onActivate?: () => void }> = ({
  status,
  rowId,
  onActivate,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const p = getPalette(status);
  const { className, style } = getStatusLabelPresentation(status, p.accent, prefersReducedMotion);
  const clickable = isExpandableResult(status) && !!onActivate;

  return (
    <span
      {...{ [FLIP_ROW_MARKER_ATTRIBUTE]: rowId }}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        position: "relative",
      }}
    >
      <span className={className} style={style}>
        {p.statusLabel}
      </span>
      {isExpandableResult(status) && (
        <span
          aria-hidden
          data-testid="rrp-expand-chevron"
          className="ms-Icon ms-Icon--ChevronDown"
          style={{ fontSize: 14, color: p.accent, display: "inline-flex", alignItems: "center", lineHeight: 1 }}
        />
      )}
      {clickable && (
        <span
          aria-hidden
          data-testid="rrp-status-end-clickable-overlay"
          onClick={onActivate}
          style={{ position: "absolute", inset: 0, cursor: "pointer" }}
        />
      )}
    </span>
  );
};

/**
 * Toggles `id`'s membership in `current` — the same "flip one id" semantics Fluent's Accordion
 * applies internally before it ever calls `onAccordionToggle`.
 */
function toggleOpenItem(current: string[], id: string): string[] {
  return current.includes(id) ? current.filter((existingId) => existingId !== id) : [...current, id];
}

/**
 * Single source of truth for "which of these openItems are actually allowed to expand" (only
 * VERIFIED/FAILED/ERROR rows). Matches by `ruleId`, not the per-instant `id`. Both `handleToggle`
 * and `handleManualToggle` funnel through this so there is exactly one allow/deny rule.
 */
function filterExpandableOpenItems(results: RunResult[], openItems: string[]): string[] {
  return openItems.filter((id) => {
    const result = results.find((r) => r.ruleId === id);
    return !!result && isExpandableResult(result.info.status);
  });
}

interface ResultsProgress {
  total: number;
  percent: number;
  complete: boolean;
}

/**
 * Percent complete for the streaming footer progress bar, derived purely from the current
 * `results` rows — never the per-batch SSE `progress` event (that reports a single batch's own
 * completed/total, which restarts every chunk with multi-batch streaming). `isNonTerminal`
 * (RUNNING/QUEUED) is the gate for "not yet counted" — CANCELED counts toward the numerator like
 * any other terminal status; `isExpandableResult` is a different predicate and must never be
 * substituted here. Recomputed from the live array every call, since dismissing a row mid-run
 * changes both the numerator and denominator.
 */
function computeResultsProgress(results: RunResult[]): ResultsProgress {
  const total = results.length;
  if (total === 0) {
    // Never divide by zero — also doubles as the "no run at all" case the phase hook below hides.
    return { total: 0, percent: 0, complete: false };
  }
  const terminalCount = results.reduce((count, r) => (isNonTerminal(r.info.status) ? count : count + 1), 0);
  return { total, percent: (terminalCount / total) * 100, complete: terminalCount === total };
}

type ResultsProgressPhase = "hidden" | "shown" | "fading";

/** Kept in sync with the `.rrp-progress-track.rrp-progress-animated` opacity transition duration
 *  in colors.css -- this is how long the bar stays mounted (fully faded) before actually unmounting. */
const PROGRESS_FADE_MS = 300;

/**
 * Owns the show/fade/hide lifecycle so `FooterPanel` (shared, presentational only) never needs to
 * know about timers or "already completed" latching. `wasInProgressRef` guards against mounting
 * already-complete (e.g. navigating back to a finished run) playing an unearned fade-out, and
 * against a plain boolean latching true forever and hiding the bar on every future re-run. Keying
 * off the current `hasRows`/`complete` means a re-run that seeds fresh rows always un-hides the bar.
 */
function useResultsProgressPhase(hasRows: boolean, complete: boolean, reducedMotion: boolean): ResultsProgressPhase {
  const [phase, setPhase] = useState<ResultsProgressPhase>(() => (hasRows && !complete ? "shown" : "hidden"));
  const wasInProgressRef = useRef(hasRows && !complete);

  useEffect(() => {
    if (!hasRows || !complete) {
      // No rows at all, or still in flight (including a re-run that just reopened this branch):
      // show whenever there is something to show, hide instantly otherwise -- never a fade here.
      wasInProgressRef.current = hasRows && !complete;
      setPhase(wasInProgressRef.current ? "shown" : "hidden");
      return undefined;
    }

    // complete === true, hasRows === true from here on.
    if (!wasInProgressRef.current) {
      // Never observed as in-progress (e.g. mounted already-settled) -- nothing to fade FROM.
      setPhase("hidden");
      return undefined;
    }

    wasInProgressRef.current = false;
    if (reducedMotion) {
      // Reduced motion: snap straight to hidden, skipping the fade entirely.
      setPhase("hidden");
      return undefined;
    }
    setPhase("fading");
    const timer = setTimeout(() => setPhase("hidden"), PROGRESS_FADE_MS);
    return () => clearTimeout(timer);
  }, [hasRows, complete, reducedMotion]);

  return phase;
}

/**
 * `itemStart`/`itemEnd` render props. `createRenderItemStart`/`createRenderItemEnd` are
 * module-level factories (not components) that close over `onActivate` and return the actual
 * render-prop function; the call site memoizes the factory's result via `useMemo`. `itemBadge`/
 * `itemContent` below follow the same factory shape.
 *
 * `createRenderItemStart`'s wrapper is `aria-hidden`, matching `RunStatusIndicator`'s own markup —
 * this keeps the click target clear of `S1082`/`S6848` (an interactive handler on a non-interactive
 * element needs a native/ARIA-interactive role, which would trip the same `S6819` finding the
 * dismiss button already carries, or to be `aria-hidden`).
 */
function createRenderItemStart(onActivate: (item: AccordionItemData<RunResult>) => void) {
  return function renderItemStart(item: AccordionItemData<RunResult>) {
    const clickable = isExpandableResult(item.data.info.status);
    return (
      <span
        aria-hidden
        onClick={clickable ? () => onActivate(item) : undefined}
        style={{ display: "inline-flex", cursor: clickable ? "pointer" : undefined }}
      >
        <RunStatusIndicator status={item.data.info.status} />
      </span>
    );
  };
}

function createRenderItemEnd(onActivate: (item: AccordionItemData<RunResult>) => void) {
  return function renderItemEnd(item: AccordionItemData<RunResult>) {
    return <RunStatusEnd status={item.data.info.status} rowId={item.id} onActivate={() => onActivate(item)} />;
  };
}

/**
 * `itemBadge` render prop — the dismiss/delete control. Same module-level factory shape as above,
 * closing over `onDelete` only.
 *
 * `role="button"` (not a native `<button>`) is required: this renders inside `AccordionHeader`'s
 * own children, whose root is itself a native `<button>` — nesting a second one would be invalid
 * HTML and break the header's own click-to-expand behavior. Left unfixed on purpose (S6819).
 */
function createRenderItemBadge(onDelete: (ruleId: string) => void) {
  return function renderItemBadge(item: AccordionItemData<RunResult>) {
    // A row still streaming (RUNNING/QUEUED) must not be dismissible. `!isNonTerminal(...)`, not
    // `isExpandableResult`, is the right gate: CANCELED is terminal but not expandable, yet still
    // dismissible.
    if (isNonTerminal(item.data.info.status)) return null;
    return (
      <span
        className="rrp-delete ms-Icon ms-Icon--Delete"
        role="button"
        tabIndex={0}
        title={formatMessage("cof.base.dismiss")}
        aria-label={formatMessage("cof.base.dismiss")}
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onDelete(item.id);
          }
        }}
      />
    );
  };
}

/** `itemContent` render prop — same module-level factory shape as the others, closing over
 * `onShowOnDocument` only. */
function createRenderItemContent(onShowOnDocument?: (payload: CoObjectSummary) => void) {
  return function renderItemContent(item: AccordionItemData<RunResult>) {
    // Content is returned regardless of isExpanded so Fluent's Collapse exit animation has
    // something to animate while closing (a closed row still never mounts, via unmountOnExit).
    // Defense-in-depth: refuse non-expandable rows even if isExpanded were ever true for one.
    if (!isExpandableResult(item.data.info.status)) return null;
    return <RuleResultContent result={item.data} onShowOnDocument={onShowOnDocument} />;
  };
}

export const RulesResultPanel: React.FC<RulesResultPanelProps> = ({
  results = [],
  onDelete,
  onShowOnDocument,
  onBack,
}) => {
  const [expanded, setExpanded] = useState<string[]>([]);

  // Prunes a dismissed row's id from `expanded` (harmless if left, but avoids unbounded growth
  // over a long session) whenever `results` changes, reusing the same `filterExpandableOpenItems`
  // gate — this effect only removes ids, never adds. Returns the same array reference when
  // nothing needs pruning, avoiding an extra render.
  useEffect(() => {
    setExpanded((current) => {
      const pruned = filterExpandableOpenItems(results, current);
      return pruned.length === current.length ? current : pruned;
    });
  }, [results]);

  // useFlipReorder searches for row marker elements within this container — the same `.rrp-panel`
  // div that already wraps AccordionListPanel, no new DOM node.
  const containerRef = useRef<HTMLDivElement>(null);
  // A separate call from RunStatusEnd's own per-row subscription — cheap, consistent with the
  // existing pattern of calling this hook directly rather than threading it through props.
  const prefersReducedMotionForOrdering = usePrefersReducedMotion();

  // Row identity is the rule's own ruleId, not the per-instant id (regenerated on every status
  // change) — ruleId never changes across a run, so React never remounts the row and `expanded`
  // never silently loses it.
  const items = useMemo<AccordionItemData<RunResult>[]>(
    () => results.map((r) => ({ id: r.ruleId, title: r.title, data: r })),
    [results]
  );

  // Memoized independently of `items`'s identity so an unrelated re-render hands useFlipReorder
  // the same array reference, avoiding a redundant measurement pass.
  const rowIds = useMemo(() => items.map((item) => item.id), [items]);
  useFlipReorder({
    containerRef,
    rowIds,
    enabled: RESULTS_ORDERING_MODE === "live",
    prefersReducedMotion: prefersReducedMotionForOrdering,
  });

  // Only terminal-result rows (VERIFIED/FAILED/ERROR) may ever be expanded — filtering the whole
  // array (rather than diffing) always reflects the current `results`, so a row that flips
  // RUNNING -> terminal becomes toggleable on the very next click with no remount needed.
  const handleToggle = (_event: any, data: { openItems: string[] }) => {
    setExpanded(filterExpandableOpenItems(results, data.openItems));
  };

  // The indicator/status-label/chevron click path: computes the same `openItems` array Fluent's
  // own native header toggle would report for this one row, then funnels it through the same
  // `filterExpandableOpenItems` gate `handleToggle` uses — one definition of "which rows may
  // expand", never a forked copy.
  const handleManualToggle = (item: AccordionItemData<RunResult>) => {
    if (!isExpandableResult(item.data.info.status)) return;
    setExpanded(filterExpandableOpenItems(results, toggleOpenItem(expanded, item.id)));
  };

  // The memoized value is a plain render-prop function, not a component, so this doesn't
  // reintroduce S6478 regardless of how often handleManualToggle's identity changes.
  const renderItemStart = useMemo(() => createRenderItemStart(handleManualToggle), [handleManualToggle]);
  const renderItemEnd = useMemo(() => createRenderItemEnd(handleManualToggle), [handleManualToggle]);
  // itemBadge stays undefined (not a no-op function) when onDelete isn't provided —
  // AccordionListPanel treats a missing itemBadge as "no badge column".
  const renderItemBadge = useMemo(() => (onDelete ? createRenderItemBadge(onDelete) : undefined), [onDelete]);
  const renderItemContent = useMemo(() => createRenderItemContent(onShowOnDocument), [onShowOnDocument]);

  const resultsProgress = useMemo(() => computeResultsProgress(results), [results]);
  // A separate usePrefersReducedMotion() call — same rationale as prefersReducedMotionForOrdering:
  // cheap, independent subscription per concern.
  const prefersReducedMotionForProgress = usePrefersReducedMotion();
  const progressPhase = useResultsProgressPhase(
    resultsProgress.total > 0,
    resultsProgress.complete,
    prefersReducedMotionForProgress
  );
  // FooterPanel is purely presentational: omitting the prop entirely (rather than passing e.g.
  // `{ percent, visible: false }`) is what unmounts the strip once its fade has run, and is also
  // why a fresh run reappearing can never latch hidden.
  const footerProgressBar: FooterProgressBarProps | undefined =
    progressPhase === "hidden"
      ? undefined
      : {
          percent: resultsProgress.percent,
          fading: progressPhase === "fading",
          reducedMotion: prefersReducedMotionForProgress,
        };

  return (
    <>
      <style>{`
        .rrp-delete { visibility: hidden; color: #b3261e; cursor: pointer; font-size: 13px; margin-left: 6px; }
        .alp-item:hover .rrp-delete { visibility: visible; }
        .rrp-panel .alp-item.alp-expanded { background-color: #ffffff !important; }
        .rrp-panel .alp-item.alp-expanded .fui-AccordionHeader,
        .rrp-panel .alp-item.alp-expanded .fui-AccordionPanel { background-color: #ffffff !important; }
        .rrp-panel .alp-item.alp-expanded > .fui-AccordionPanel { box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
        .rrp-consistency .fui-AccordionPanel { box-shadow: none !important; }
        /* AccordionListPanel always renders Fluent's native trailing chevron; hide it here (pure
           CSS) and render our own conditionally in RunStatusEnd, so only terminal-result rows
           show one. */
        .rrp-panel .fui-AccordionHeader__expandIcon { display: none; }
      `}</style>

      <HeaderPanel applyPadding>{formatMessage("cof.validation.title.validationresults")}</HeaderPanel>

      <div
        ref={containerRef}
        className="rrp-panel"
        style={{
          maxHeight: "calc(100vh - 130px)",
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: 8,
          paddingBottom: 8,
          // Reserves the vertical scrollbar's track width up front, so expanding/collapsing a card
          // never flashes the scrollbar or shifts rows horizontally. Ignored gracefully on engines
          // that don't support scrollbar-gutter.
          scrollbarGutter: "stable",
        }}
      >
        <AccordionListPanel<RunResult>
          items={items}
          filteredItems={items}
          expanded={expanded}
          searchQuery=""
          actionGroups={[]}
          onAccordionToggle={handleToggle}
          onFilter={() => {}}
          withFilter={false}
          // The shared AccordionListPanel's content-area <hr> is unwanted here; suppressed for
          // this results panel only (other callers keep the default).
          withContentDivider={false}
          expandIconPosition="end"
          // Explicit bold (rather than relying on TruncatedText's own default) so the requirement
          // is directly testable at this call site.
          headerStyle={{ fontWeight: "bold" }}
          itemStart={renderItemStart}
          itemBadge={renderItemBadge}
          itemEnd={renderItemEnd}
          itemContent={renderItemContent}
          // Forwarded to AccordionListPanel's own <FooterPanel>. `footer` below is now the button
          // itself (not its own <FooterPanel> wrapper) — a nested <FooterPanel> would be an
          // out-of-flow descendant of AccordionListPanel's own one, contributing zero height to it
          // while painting over its progress strip. With a single <FooterPanel>, sized to fit the
          // button, the strip lands on its real, visible top edge.
          footerProgressBar={footerProgressBar}
          footer={<PrimaryButton onClick={onBack}>{formatMessage("cof.base.back")}</PrimaryButton>}
        />
      </div>
    </>
  );
};
